import { useEffect, useState } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import styled from 'styled-components';
import Modal from '../components/advance/Modal';
import Button from '../components/core/EPL_Button_Think';
import CheckField from "../components/form/CheckField";

const IframeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 90vw;

  .pdf-viewer {
    width: 80vw;
    height: 75vh;
    border: none;
  }

  .modal-header {
    align-items: center;
    background-color: #eeeeee;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: center;
    padding: 4px;
  }

  .zoom-button {
    padding: 0px 2px;
  }

  .button-group {
    display: flex;
  }

  .error-message {
    text-align: center;
  }
`;

function downloadPDF(pdfData, pdfFileName) {
    fetch(pdfData)
        .then((response) => response.blob())
        .then((blob) => {
            const downloadLink = document.createElement("a");
            const fileName = pdfFileName + ".pdf";
            const url = URL.createObjectURL(blob);

            downloadLink.href = url;
            downloadLink.download = fileName;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(url);
        })
        .catch((error) => {
            console.error("Error downloading the PDF: ", error);
        });
}

const PdfModal = ({
    isOpen,
    onClose,
    url,
    onOk,
    okButtonText = "OK",
    showCheckbox = false,
    checkboxLabel = "",
    onSecondButtonClick,
    secondButtonText = "Disagree",
    showSecondButton = false,
}) => {
    const zoomPluginInstance = zoomPlugin();
    const pageNavigationPluginInstance = pageNavigationPlugin();
    const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;
    const { CurrentPageLabel } = pageNavigationPluginInstance;

    const [isLastPage, setIsLastPage] = useState(false);
    const [error, setError] = useState(null);
    const [consentChecked, setConsentChecked] = useState(false);

    const handleError = (error) => {
        setError(`${error.message} An error occurred while loading the PDF. Please download and view the document before proceeding.`);
        return error.message;
    };

    const handleCheckboxToggle = () => {
        setConsentChecked((prev) => !prev);
    };

    // inside PdfModal
    useEffect(() => {
        setIsLastPage(false);
        setError(null);
        setConsentChecked(false);
    }, [url]);

    const isOkButtonEnabled =
        (isLastPage && (showCheckbox ? consentChecked : true)) || !!error;

    return (
        <Modal open={isOpen} closeOnClickOutside={false} onClose={onClose}>
            <IframeWrapper>
                <div className="modal-header">
                    <div className="zoom-button">
                        <ZoomOutButton />
                    </div>
                    <div className="zoom-button">
                        <ZoomPopover />
                    </div>
                    <div className="zoom-button">
                        <ZoomInButton />
                    </div>
                    <div className="zoom-button">
                        <CurrentPageLabel>
                            {({ currentPage, numberOfPages }) => {
                                if (currentPage + 1 === numberOfPages) {
                                    setIsLastPage(true);
                                } else {
                                    setIsLastPage(false);
                                }
                                return (
                                    <span>{`${currentPage + 1} of ${numberOfPages}`}</span>
                                );
                            }}
                        </CurrentPageLabel>
                    </div>
                </div>

                {!error ? (
                    url ?(
                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                        <div className="pdf-viewer">
                            <Viewer
                                key={url}   // Force re-render when url changes
                                fileUrl={url}
                                defaultScale={0}
                                plugins={[zoomPluginInstance, pageNavigationPluginInstance]}
                                renderError={handleError}
                            />
                        </div>
                    </Worker>
                ) : null
            ):(
                    <div className="error-message">
                        {error}
                        <Button
                            text={"Download PDF"}
                            onClick={() => downloadPDF(url, "Download")}
                        />
                    </div>
                )}

                {showCheckbox && (
                    <CheckField
                        id="basic-checkfield"
                        checked={consentChecked}
                        label={checkboxLabel}
                        style={{ fontSize: "1.4rem", marginTop: "10px" }}
                        onClick={handleCheckboxToggle}
                    />
                )}

                <div className="button-group">
                    <Button
                        text={okButtonText}
                        onClick={onOk}
                        disabled={!isOkButtonEnabled}
                    />
                    {showSecondButton && (
                        <Button
                            text={secondButtonText}
                            onClick={onSecondButtonClick}
                            disabled={!isOkButtonEnabled}
                        />
                    )}
                </div>
            </IframeWrapper>
        </Modal>
    );
};

export default PdfModal;
